import { useMemo, useState, useEffect } from "react";
//--------------------------------------------------------------------------------------------
import Items from "src/components/common/frame/items";
import { useMyStore } from "src/components/main/mainStore";
import { onlyUnique, myAxios, objAscSort, getTimeStamp } from "src/components/common/functions";
//--------------------------------------------------------------------------------------------
export default function Leads({ tabTitle }){
    const setShowLoader = useMyStore(state => state.setShowLoader);
    const userNames = useMyStore(state => state.userNames);
    const setUserNames = useMyStore(state => state.setUserNames);
    const organizationNames = useMyStore(state => state.organizationNames);
    const setOrganizationNames = useMyStore(state => state.setOrganizationNames); 
    const contactNames = useMyStore(state => state.contactNames);
    const setContactNames = useMyStore(state => state.setContactNames);
    const opportunities = useMyStore(state => state.opportunities);
    const setOpportunities = useMyStore(state => state.setOpportunities);
    
    const hashKeyName = "opportunityHashKey";
    const createLink = "opportunities/createOppo.php";
    const readLink = "opportunities/readOppo.php";
    const updateLink = "opportunities/updateOppo.php";
    const deleteLink = "opportunities/deleteOppo.php";
    const sqlTable = "opportunities"
    const itemLabel = "opportunity"
    const hashKeyLabel = "opportunityHashKey"
    const filesKey = "oppoFiles"
    const numItemsPerColumn = 10;
    const [updateDateTime, setUpdateDateTime] = useState({
        [tabTitle]: opportunities.updatedAt === null?getTimeStamp():opportunities.updatedAt,
        "userNames": userNames.updatedAt === null?getTimeStamp():userNames.updatedAt, 
        "organizationNames": organizationNames.updatedAt === null?getTimeStamp():organizationNames.updatedAt, 
        "contactNames": contactNames.updatedAt === null?getTimeStamp():contactNames.updatedAt, 
    });
    //--------------------------------------------------------
    const usersMap = useMemo(() => Object.fromEntries(userNames.data.map(user => [user.email, user.userName])), [userNames.data]);
    const orgsMap = useMemo(() => Object.fromEntries(organizationNames.data.map(org => [org.organizationHashKey, org.organizationName])), [organizationNames.data]);
    const contactsMap = useMemo(() => Object.fromEntries(contactNames.data.map(contact => [contact.contactHashKey, contact.contactName])), [contactNames.data]);
    const itemsExtended = useMemo(() => opportunities.data.map(item => 
        ({
            ...item,
            organizationName: orgsMap[item.organizationHashKey] || "",
            userResponsible: usersMap[item.userResponsibleEmail]?.replace(/ {2}/g, " ") || "",
            userResponsible2: usersMap[item.userResponsible2Email]?.replace(/ {2}/g, " ") || "",
            requestor1: contactsMap[item.requestorHashKey]?.replace(/ {2}/g, " ") || "",
            requestor2: contactsMap[item.requestor2HashKey]?.replace(/ {2}/g, " ") || "",
            requestor3: contactsMap[item.requestor3HashKey]?.replace(/ {2}/g, " ") || "",
            creator: usersMap[item.creatorEmail]?.replace(/ {2}/g, " ") || "",
            updater: usersMap[item.updaterEmail]?.replace(/ {2}/g, " ") || "",
            dateCreated: item.dateCreated ?? "",
            dateUpdated: item.dateUpdated ?? "",
            
            projectName: item.projectName,

            oppoFiles: JSON.parse(item.oppoFiles),

            rfq: JSON.parse(item.oppoFiles).rfq,
            qualifications: JSON.parse(item.oppoFiles).qualificationFiles,
            proposals: JSON.parse(item.oppoFiles).oppoProposalFiles,
            contracts: JSON.parse(item.oppoFiles).oppoContractFiles,
            emailRecords: JSON.parse(item.oppoFiles).emailRecordsFiles,

            rfqString: JSON.parse(item.oppoFiles).rfq?.map(file => file.name).join(", "),
            qualificationsString: JSON.parse(item.oppoFiles).qualificationFiles?.map(file => file.name).join(", "),
            proposalsString: JSON.parse(item.oppoFiles).oppoProposalFiles?.map(file => file.name).join(", "),
            contractsString: JSON.parse(item.oppoFiles).oppoContractFiles?.map(file => file.name).join(", "),
            emailRecordsString: JSON.parse(item.oppoFiles).emailRecordsFiles?.map(file => file.name).join(", "),
        })
    ), 
    [opportunities.data, usersMap, contactsMap, orgsMap]);
    const dataFrame = useMemo(() => [
        {
            title: "Opportunity Name",
            key: "opportunityName",
            type: "text",
            position: "title",
            editable: true
        },
        {//Link
            title: "Organization Name",
            key: "organizationName",
            selectKey: "organizationHashKey",
            type: "select",
            options: organizationNames.data.sort((a, b) => objAscSort(a, b, "organizationName")).map(org => ({label: org.organizationName, value: org.organizationHashKey})),
            filterOptions: itemsExtended.map(item => item.organizationName).filter(onlyUnique).sort().map(value => ({label: value, value: value})), 
            position: "title",
            preventOpenDetail: true,
            valueType: "internalLink",
            target: "/Organizations/{{selectValue}}",
            editable: true
        },
        {
            title: "Requestor 1",
            key: "requestor1",
            selectKey: "requestorHashKey",
            type: "select",
            options: contactNames.data.sort((a, b) => objAscSort(a, b, "contactName")).map(contact => ({label: contact.contactName, value: contact.contactHashKey, organizationHashKey: contact.organizationHashKey})),
            filterOptions: itemsExtended.map(item => item.requestor1).filter(onlyUnique).sort().map(value => ({label: value, value: value})), 
            position: "detail",
            valueType: "internalLink",
            target: "/Contacts/{{selectValue}}",
            editable: true
        },
        {
            title: "Requestor 2",
            key: "requestor2",
            selectKey: "requestor2HashKey",
            type: "select",
            options: contactNames.data.sort((a, b) => objAscSort(a, b, "contactName")).map(contact => ({label: contact.contactName, value: contact.contactHashKey, organizationHashKey: contact.organizationHashKey})),
            filterOptions: itemsExtended.map(item => item.requestor2).filter(onlyUnique).sort().map(value => ({label: value, value: value})), 
            position: "detail",
            editable: true
        },
        {
            title: "Requestor 3",
            key: "requestor3",
            selectKey: "requestor3HashKey",
            type: "select",
            options: contactNames.data.sort((a, b) => objAscSort(a, b, "contactName")).map(contact => ({label: contact.contactName, value: contact.contactHashKey, organizationHashKey: contact.organizationHashKey})),
            filterOptions: itemsExtended.map(item => item.requestor3).filter(onlyUnique).sort().map(value => ({label: value, value: value})), 
            position: "detail",
            editable: true
        },
        {//%
            title: "Probability",
            key: "probability",
            type: "number",
            suffix: "%",
            step: "0.01",
            min: 0,
            max: 100,
            position: "title",
            editable: true
        },
        {
            title: "Bid Amount",
            key: "bidAmount", 
            type: "number",
            step: "0.01",
            position: "title",
            editable: true
        },
        {
            title: "Bid Type",
            key: "bidType",
            type: "select",
            options: [
                "Fixed Bid", 
                "Per Day"
            ].sort().map(value => ({label: value, value: value})),
            filterOptions: itemsExtended.map(item => item.bidType).filter(onlyUnique).sort().map(value => ({label: value, value: value})), 
            position: "title",
            editable: true
        },
        {
            title: "Opportunity Category",
            key: "opportunityCategory",
            type: "select",
            options: [
                "Locating", 
                "Locating & CCTV", 
                "Locating & Potholing", 
                "Locating & Surveying",
                "Potholing", 
                "CCTV", "CCTV & Potholing", 
                "Surveying", "Scanning", 
                "SoCal Opportunities", 
                "Lunch & Learn Session", 
                "New Business", 
                "Proposal"
            ].sort().map(value => ({label: value, value: value})),
            filterOptions: itemsExtended.map(item => item.opportunityCategory).filter(onlyUnique).sort().map(value => ({label: value, value: value})), 
            position: "title",
            editable: true
        },
        {//a
            title: "Current State",
            key: "currentState",
            type: "select", 
            options: [
                "OPEN", 
                "WON", 
                "LOST", 
                "SUSPENDED", 
                "ABANDONED"
            ].sort().map(value => ({label: value, value: value})),
            filterOptions: itemsExtended.map(item => item.currentState).filter(onlyUnique).sort().map(value => ({label: value, value: value})), 
            position: "title",
            editable: true
        },
        {//a
            title: "Location",
            key: "location",
            type: "select",
            options:["North","South"].sort().map(value => ({label: value, value: value})),
            filterOptions: itemsExtended.map(item => item.location).filter(onlyUnique).sort().map(value => ({label: value, value: value})), 
            position: "title",
            editable: true
        },
        {
            title: "Project Name",
            type: "select", 
            options: itemsExtended.map(item => item.projectName).filter(onlyUnique).sort().map(value => ({label: value, value: value})),
            key: "projectName",
            position: "detail"
        },
        {
            title: "User Resposible 1",
            key: "userResponsible",
            selectKey: "userResponsibleEmail",
            type: "select", 
            options: userNames.data.filter(user => user.projects !== "no").sort((a, b) => objAscSort(a, b, "firstName")).map(user => ({label: user.userName, value: user.email})),
            filterOptions: itemsExtended.map(item => item.userResponsible).filter(onlyUnique).sort().map(value => ({label: value, value: value})), 
            valueType: "userInfo",
            userInfoName: "userResponsible",
            userInfoEmail: "userResponsibleEmail",
            position: "detail",
            editable: true
        },
        {
            title: "User Resposible 2",
            key: "userResponsible2",
            selectKey: "userResponsible2Email",
            type: "select", 
            options: userNames.data.filter(user => user.projects !== "no").sort((a, b) => objAscSort(a, b, "firstName")).map(user => ({label: user.userName, value: user.email})),
            filterOptions: itemsExtended.map(item => item.userResponsible).filter(onlyUnique).map(value => ({label: value, value: value})), 
            valueType: "userInfo",
            userInfoName: "userResponsible2",
            userInfoEmail: "userResponsible2Email",
            position: "detail",
            editable: true
        },
        {
            title: "Actual Close Date",
            key: "actualCloseDate",
            type: "date", 
            position: "detail",
            editable: true
        },
        {
            title: "Creator",
            key: "creator",
            type: "select",
            filterOptions: itemsExtended.map(item => item.creator).filter(onlyUnique).sort().map(value => ({label: value, value: value})),
            valueType: "userInfo",
            userInfoName: "creator",
            userInfoEmail: "creatorEmail",
            position: "detail",
            conditionalPosition: true,
            suffix: "dateCreated"
        },
        {
            title: "Date Created",
            key: "dateCreated",
            type: "date",
        },
        {
            title: "Updator",
            key: "updater",
            type: "select",
            filterOptions: itemsExtended.map(item => item.updater).filter(onlyUnique).sort().map(value => ({label: value, value: value})),
            valueType: "userInfo",
            userInfoName: "updater",
            userInfoEmail: "updaterEmail",
            position: "detail",
            conditionalPosition: true,
            suffix: "dateUpdated"
        },
        {
            title: "Date Updated",
            key: "dateUpdated",
            type: "date"
        },
        {
            title: "RFQ",
            folderIdLabel: "rfqFolderId",
            folderKey: "rfq",
            key: "rfq",
            exportKey: "rfqString",
            filterKey: "rfqString",
            type: "file",
            multiple: true,
            position: "files",
            noShow: "Edit",
           
            editable: true
        },
        {
            title: "Qualifications",
            folderIdLabel: "qualificationFolderId",
            folderKey: "qualificationFiles",
            key: "qualifications",
            exportKey: "qualificationsString",
            filterKey: "qualificationsString",
            type: "file",
            multiple: true,
            position: "files",
            noShow: "Edit",
            editable: true
        },
        {
            title: "Proposals",
            folderIdLabel: "oppoProposals",
            folderKey: "oppoProposalFiles",
            key: "proposals",
            exportKey: "proposalsString",
            filterKey: "proposalsString",
            type: "file",
            multiple: true,
            position: "files",
            noShow: "Edit",
            editable: true
        },
        {
            title: "Contracts",
            folderIdLabel: "oppoContracts",
            folderKey: "oppoContractFiles",
            key: "contracts",
            exportKey: "contractsString",
            filterKey: "contractsString",
            type: "file",
            multiple: true,
            position: "files",
            noShow: "Edit",
            editable: true
        },
        {
            title: "Email Records",
            folderIdLabel: "emailRecordsFolderId",
            folderKey: "emailRecordsFiles",
            key: "emailRecords",
            exportKey: "emailRecordsString",
            filterKey: "emailRecordsString",
            type: "file",
            multiple: true,
            position: "files",
            noShow: "Edit",
            editable: true
        },
        {
            title: "Background",
            key: "background",
            type: "textarea",
            position: "detail",
            editable: true
        },
    ], 
    [userNames.data, contactNames.data, organizationNames.data, itemsExtended]);
    useEffect(() => {
        if(
            updateDateTime["userNames"] === userNames.updatedAt || 
            updateDateTime["organizationNames"] === organizationNames.updatedAt || 
            updateDateTime["contactNames"] === contactNames.updatedAt
        ) return;
        let isMounted = true;
        const controller = new AbortController();
        const getData = async() => {
            //--------------------------------------------------------
            setShowLoader(true);
            const res = await myAxios("opportunities/readSides.php", undefined, undefined, controller.signal);
            if(!isMounted) return;
            setShowLoader(false);
            if(!res) return;
            //--------------------------------------------------------
            setUserNames(res.data.users, updateDateTime["userNames"]);
            setOrganizationNames(res.data.orgs, updateDateTime["organizationNames"]);
            setContactNames(res.data.contacts, updateDateTime["contactNames"]);
        }
        getData();
        return () => {
            isMounted = false;
            setShowLoader(false);
            controller.abort();
        }
    }, [updateDateTime, userNames.updatedAt, organizationNames.updatedAt, contactNames.updatedAt, setContactNames, setOrganizationNames, setShowLoader, setUserNames])

    useEffect(() => { 
    if (opportunities?.data?.length > 0) { 
        console.log("Item sample from useEffect:", opportunities.data[0]);
    }
    }, [opportunities.data]);

    //--------------------------------------------------------
    return(
        <Items
            updateDateTime = {updateDateTime}
            setUpdateDateTime = {setUpdateDateTime}
            tabTitle = {tabTitle}
            postUrl = {updateLink}
            dataFrame = {dataFrame}
            readLink = {readLink}
            createLink = {createLink}
            deleteLink = {deleteLink}
            numItemsPerColumn = {numItemsPerColumn}
            hashKeyName = {hashKeyName}
            items = {opportunities}
            setItems = {setOpportunities}
            itemsExtended = {itemsExtended}
            defaultSortKey = {"opportunityName"}
            width = {"40rem"}
            minWidth = {"36rem"}

            sqlTable = {sqlTable}
            itemLabel = {itemLabel}
            hashKeyLabel =  {hashKeyLabel}
            filesKey = {filesKey}
            contacts = {contactNames.data}
        />
    )
}